/**
 * useIconClick — Premium icon micro-animation on click.
 *
 * Adds a CSS animation class to an icon wrapper when clicked,
 * then removes it after the animation completes so it can be
 * re-triggered on the next click.
 *
 * Animation types (each is a distinct directional motion):
 *   - bounce  — deep spring press (scale 0.65 → 1.18 → 1.0)
 *   - spin    — smooth 180° gear rotation
 *   - jiggle  — bell pendulum swing from top pivot
 *   - pulse   — gravity bounce-drop with squash/stretch
 *   - wiggle  — zoom-peek lift (search magnifying glass)
 *   - flip    — 3D depth pop (perspective push-in / pop-out)
 *   - rotate  — quarter-turn twist (90° and back)
 *
 * @example
 *   const iconRef = useIconClick('spin');
 *   <span ref={iconRef} onClick={...}><Settings /></span>
 *
 * @since 2.2.0
 */
import { useCallback, useRef } from 'react';

export type IconAnimType = 'bounce' | 'spin' | 'jiggle' | 'pulse' | 'wiggle' | 'flip' | 'rotate';

const ANIM_CLASSES: Record<IconAnimType, string> = {
    bounce: 'icon-anim-bounce',
    spin: 'icon-anim-spin',
    jiggle: 'icon-anim-jiggle',
    pulse: 'icon-anim-pulse',
    wiggle: 'icon-anim-wiggle',
    flip: 'icon-anim-flip',
    rotate: 'icon-anim-rotate',
};

const ANIM_DURATIONS: Record<IconAnimType, number> = {
    bounce: 550,
    spin: 350,
    jiggle: 700,
    pulse: 500,
    wiggle: 500,
    flip: 500,
    rotate: 500,
};

/**
 * Returns a ref to attach to the icon wrapper element.
 * When the element (or its parent) is clicked, the icon
 * plays the specified animation.
 */
export function useIconClick<T extends HTMLElement = HTMLSpanElement>(
    animType: IconAnimType = 'bounce'
) {
    const ref = useRef<T>(null);
    const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

    const trigger = useCallback(() => {
        const el = ref.current;
        if (!el) return;

        const cls = ANIM_CLASSES[animType];
        const dur = ANIM_DURATIONS[animType];

        // Remove any existing animation (allows re-triggers)
        el.classList.remove(cls);
        // Force reflow to restart animation
        void el.offsetWidth;
        el.classList.add(cls);

        // Clear previous timeout if click happens during animation
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
            el.classList.remove(cls);
        }, dur + 50); // Small buffer for safety
    }, [animType]);

    return { ref, trigger };
}

/**
 * Standalone trigger function for one-off use without a ref.
 * Finds the first child SVG inside the target element and animates it.
 */
export function triggerIconAnim(
    element: HTMLElement | null,
    animType: IconAnimType = 'bounce'
) {
    if (!element) return;

    // Find the icon wrapper (first child span, or the element itself)
    const target = element.querySelector('.icon-click-target') as HTMLElement || element;
    const cls = ANIM_CLASSES[animType];
    const dur = ANIM_DURATIONS[animType];

    target.classList.remove(cls);
    void target.offsetWidth;
    target.classList.add(cls);

    setTimeout(() => target.classList.remove(cls), dur + 50);
}
